import { Alert, Button, ButtonRow, Col, Row } from '@op/opds.opux';
import { IconAdd } from '@op/opds.opux-icons/action';
import { IconError } from '@op/opds.opux-icons/indicator';
import { IconDown } from '@op/opds.opux-icons/navigation';
import { Modal } from '@op/opds.opux-modal';
import { useFormValidationAlert } from '@op/luna.yaweb-common-js/FormValidationAlert';
import React, { useState } from 'react';
import { Message, useLocales, useMessageGetter } from '@messageformat/react';
import {
  IO_CHANGE_SHOWCONTACTINFO,
  IO_CHANGE_SHOWINSURANCEERRORMESSAGE
} from '../../constants/actionTypes';
import { NewInsuranceOfferRequest, useInsuranceOffer } from '../../hooks';
import { analytics } from '../../utils';
import AddNewInsurance from './AddNewInsurance';
import ContactInfo from './ContactInfo';
import InsuranceQuestionsTile from './InsuranceQuestionsTile';
import InsuranceTile from './InsuranceTile';
import Api from '../../api';
import Valid from "@op/opds.validate"

const InsuranceSelector = (props) => {
  const [language] = useLocales();
  const { showErrors } = useFormValidationAlert();
  const getMessage = useMessageGetter();

  const [isSubmitActive, setSubmitActive] = useState(false);
  const {
    state,
    validateInsuranceAndContactInfo,
    validateAllInsurance,
    validateAllContactInfo,
  } = useInsuranceOffer();
  const { sendNewReport, getProductDetails, getProductData } =
    NewInsuranceOfferRequest();
  const { visitorAction } = analytics();

  const {
    tiles,
    showInsurances,
    showContactInfo,
    selectedInsurances,
    showExceptionMessage,
    showInsuranceErrorMessage,
    contactInfo
  } = state;

  const hasSendInsuranceTrue = selectedInsurances.some((item) => item.sendInsurance);

  const handleSmoothScrollToRef = () => {
    visitorAction({
      language,
      error: null,
      phaseCategory: 'step',
      pageName: 'vakuutettavan-kohteen-tiedot',
      products: getProductData(),
      phase: '2. vakuutettavan kohteen tiedot',
    });
    setTimeout(function () {
      document
        .getElementById('insurances-header-panel')
        .scrollIntoView({ behavior: 'smooth' });
    }, 500);
  };

  const handleSmoothScrollToRef2 = () => {
    visitorAction({
      language,
      error: null,
      phaseCategory: 'step',
      products: getProductDetails(),
      phase: '3. vakuutuksen ottajan tiedot',
      pageName: 'vakuutuksen-ottajan-tiedot',
    });
    document
      .getElementById('contactInfoIdRef')
      .scrollIntoView({ behavior: 'smooth' });
  };

  const ibRef = React.createRef();

  const closeDialog = (event) => {
    if (event.key === 'Enter') {
      ibRef.current.open();
    }
  };

  const makeInsuranceErrorMessage = () => {
    const isValid = validateAllInsurance(
      {
        type: IO_CHANGE_SHOWINSURANCEERRORMESSAGE,
      },
      getMessage
    );

    if (isValid) {
      handleSmoothScrollToRef();
      setTimeout(() => {
        const panel = document.getElementById('insurances-header-panel');
        if (panel) {
          panel.focus();
        }
      }, 550);
    } else {
      showErrors();
    }
  };

  const makeContactInfoErrorMessage = () => {
    const isValid = validateAllContactInfo(
      {
        type: IO_CHANGE_SHOWCONTACTINFO,
      },
      getMessage
    );

    if (isValid) {
      handleSmoothScrollToRef2();
      setTimeout(() => {
        const panel = document.getElementById('RS-inputs-contactform-header');
        if (panel) {
          panel.focus();
        }
      }, 550);
    } else {
      showErrors();
    }
  };

  const [customerSearched, setCustomerSearched] = useState(false)
  
  const searchCustomer = async (ssn) => {
    try {
      if (!customerSearched) {
        const ssnValid = Valid['ssn-finland'].validate(ssn) === ssn
        if (ssnValid || ssn === null) {
          setCustomerSearched(true)
          return Api.customerSearch({ ssn })
        }
      }
    } catch(err) {

    }
    return Promise.resolve()
  }

  const doSubmit = async () => {
    const { navigate } = props;
    if (!isSubmitActive && validateInsuranceAndContactInfo('all', getMessage)) {
      setSubmitActive(true);
      try {
        await searchCustomer(contactInfo.idNumber)
      } finally {
        await sendNewReport(navigate, setSubmitActive, getMessage)
      }
    } else if (!isSubmitActive) {
      setSubmitActive(true);
      showErrors();
      setSubmitActive(false);
    }
  };

  return (
    <React.Fragment>
      <p className="RS-lifeInsurance-message">
          <Message id="uiText.lifeInsuranceIssuanceMsg" />
      </p>
      {showExceptionMessage ? (
        <Alert id="alert" type="critical" contentType="dynamic">
          {getMessage('errorText.sendingProposalFailed')}
        </Alert>
      ) : null}
      <React.Fragment>
        <h2 className={'RS-insuranceselector-header'} id="insurance-title-main">
          <Message id="uiText.insuranceSelectionheader" />
        </h2>
        <Row id="InsuranceSelectorRow" role="group" aria-labelledby="insurance-title-main" aria-describedby="not-selected-notifications-error">
          {tiles.map((tile, index) => {
            return (
              <InsuranceTile
                tile={tile}
                key={tile.insuranceId}
                name={`add-new-insurance-main-${index}`}
              />
            );
          })}
        </Row>

        <ButtonRow>
          {showInsuranceErrorMessage && (
            <p className="select-insurance-error" id="not-selected-notifications-error" role="alert">
              <IconError
                color="negative"
                type="error"
                className="errorIconJatka"
              />
              {getMessage('errorText.atLeastOne')}
            </p>
          )}
          <Button
            type="button"
            id="firstJatka"
            icon={IconDown}
            iconPos="right"
            primary={!showInsurances}
            default={showInsurances}
            onClick={() => {
              makeInsuranceErrorMessage();
            }}
          >
            <Message id="buttonText.continue" />
          </Button>
        </ButtonRow>
        <div id="insurancesIdRef" />
        {showInsurances && (
          <div className="RS-inputs-grey">
            <div className="RS-inputs">
              <div>
                <div className="RS-insurance-name">
                  <h2
                    className={'RS-insurance-questions-tile-header'}
                    id="insurances-header-panel"
                    tabIndex="-1"
                  >
                    <Message id="uiTitleText.insuraceQuestions" />
                  </h2>
                </div>
                <Row
                  className="RS-inner-form-row-no-horizontal-spacings"
                  id="insurance-question-panel"
                >
                  <Col xs={12}>
                    {selectedInsurances.map((insurance, index2) => {
                      return (
                        <InsuranceQuestionsTile
                          insurance={insurance}
                          key={index2}
                          name={`insurance-${index2}`}
                        />
                      );
                    })}
                  </Col>
                </Row>

                <Row className="RS-inner-form-row-no-horizontal-spacings">
                  <Col>
                    <Row className="RS-insurance-add-new-target">
                      <div>
                        <Button
                          minified="true"
                          type="button"
                          icon={IconAdd}
                          iconLabel="add"
                          tabIndex="0"
                          size="small"
                          id="infobox-button"
                          iconProps={{
                            className: 'info-box-icons-add-new-target',
                          }}
                          onClick={() => ibRef.current.open()}
                          onKeyPress={(event) => {
                            closeDialog(event);
                          }}
                        />
                        <Modal
                          ref={ibRef}
                          id="infobox-button-dialog"
                          className="info-box-add-new-target"
                          title={<Message id="labelText.AddNewInsurance" />}
                          footer={
                            <Row id="InsuranceSelectorModalRow">
                              {tiles.map((tile, index) => {
                                return (
                                  <AddNewInsurance
                                    tile={tile}
                                    key={index}
                                    ibRef={ibRef}
                                    name={`add-new-insurance-${index}`}
                                  />
                                );
                              })}
                              <Row id="infobox-insurance-cancel-row">
                                <Button
                                  compact
                                  type="button"
                                  id="infobox-insurance-cancel"
                                  onClick={() => ibRef.current.close()}
                                >
                                  <Message id={`buttonText.cancel`} />
                                </Button>
                              </Row>
                            </Row>
                          }
                        />
                        <label
                          htmlFor="infobox-button"
                          className={'RS-insurance-add-new-target-text'}
                        >
                          <Message id="uiText.addInsurance" />
                        </label>
                      </div>
                    </Row>
                  </Col>
                </Row>
                <ButtonRow className="RS-buttonrow-to-contactinfo">
                  <Button
                    type="button"
                    primary
                    icon={IconDown}
                    iconPos="right"
                    disabled = {!hasSendInsuranceTrue}
                    onClick={() => {
                      makeContactInfoErrorMessage();
                    }}
                  >
                    <Message id="buttonText.continue" />
                  </Button>
                </ButtonRow>
              </div>
            </div>
          </div>
        )}
        <div id="contactInfoIdRef"></div>
        {showContactInfo && (
          <div className="RS-inputs-grey">
            <div className="RS-inputs">
              <div>
                <div className="RS-insurance-name">
                  <h2 id="RS-inputs-contactform-header" tabIndex="-1"className={'RS-inputs-contactform-header'}>
                    <Message id="uiText.contactNeedContactInfo" />
                  </h2>
                </div>

                <Row>
                  <Col xs={12}>
                    <Row className="RS-inner-form-row-no-horizontal-spacings">
                      <Col xs={12}>
                        <ContactInfo searchCustomer={searchCustomer} />
                        <p
                          className="RS-credit-information-text"
                          id="creditInformation"
                        >
                          <Message id="uiText.creditInformation" />
                        </p>
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <ButtonRow>
                  <Button
                    type="button"
                    finish
                    onClick={() => {
                      doSubmit();
                    }}
                    aria-describedby="creditInformation"
                  >
                    <Message id="buttonText.send" />
                  </Button>
                </ButtonRow>
              </div>
            </div>
          </div>
        )}
      </React.Fragment>
    </React.Fragment>
  );
};

export default InsuranceSelector;
